import React, {
  createContext,
  FC,
  useContext,
  useEffect,
  useState,
} from "react";

import Echo from "laravel-echo";

import { http } from "@/utils/http";

import "pusher-js";

import { AuthContext } from "@/utils/auth";
import { EnvironmentContext } from "@/utils/environment";

const PusherContext = createContext<Echo>({} as Echo);

export const usePusher = () => useContext(PusherContext);

export const PusherContextProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { config } = useContext(AuthContext);
  const { env } = useContext(EnvironmentContext);
  const baseUrl = env || "";
  
  const [echo, setEcho] = useState<Echo>({} as Echo);

  useEffect(() => {
    const initializePusher = () => {
      if (!config?.config?.pusher_key) {
        console.log("Pusher not configured. Skipping establishment");
        return {} as Echo;
      }

      try {
        return new Echo({
          authEndpoint: `${env}/api/broadcasting/auth`,
          broadcaster: "pusher",
          cluster: config.config.pusher_cluster,
          encrypted: true,
          key: config.config.pusher_key,
          authorizer: (channel, options) => ({
            authorize: async (socketId, callback) => {
              try {
                const formData = new FormData();
                formData.append("socket_id", socketId);
                formData.append("channel_name", channel.name);

                const response = await http.post("/api/broadcasting/auth", {
                  channel_name: channel.name,
                  socket_id: socketId,
                });

                callback(false, response);
              } catch (error) {
                console.error("Pusher authorization error:", error);
                callback(true, error);
              }
            },
          }),
        });
      } catch (error) {
        console.error("Failed to initialize Pusher:", error);
        return {} as Echo;
      }
    };
    if (config) {
      setEcho(initializePusher());
    }

    // Cleanup function to disconnect Pusher when the component unmounts
    return () => {
      if (echo?.disconnect) {
        echo.disconnect();
      }
    };
  }, [config, env, baseUrl]);

  return (
    <PusherContext.Provider value={echo}>{children}</PusherContext.Provider>
  );
};

export default PusherContext;
